import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import Fuse from 'fuse.js';
import { GET_GUIDES_FOR_APP } from './apps.graphql';
import { ADD_APP_LINK } from '../guides/guide.graphql';
import { SearchField } from '../components/SearchField';
import { StatusChip, TagsCell } from '../guides/GuideStatusCells';
import LoadingSpinner from '../LoadingSpinner';
import { GuideStatus } from '../__generated__/graphql';

/**
 * Picks guides to link the app to by hand. The link is the same one the guide page makes, kept on the
 * guide; this is just the way in from the app's side.
 */
export function AppGuidePicker({
  app,
  linkedGuideIds,
  onClose,
}: {
  app: { id: string; name?: string | null };
  linkedGuideIds: Set<string>;
  onClose(): void;
}) {
  const { data, loading } = useQuery(GET_GUIDES_FOR_APP);
  const [add, { error }] = useMutation(ADD_APP_LINK);
  const [filter, setFilter] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [added, setAdded] = useState<Set<string>>(new Set());

  const guides = useMemo(() => {
    const all = (data?.guides ?? []).filter((x) => x && x.docName && (showExpired || x.status !== GuideStatus.Expired));
    if (!filter) return all;
    const fuse = new Fuse(all, { shouldSort: false, threshold: 0, keys: ['docTitle', 'docName', 'mainCategory.title'] });
    return fuse.search(filter.trim()).map((x) => x.item);
  }, [data?.guides, filter, showExpired]);

  const handleAdd = async (guideId: string) => {
    try {
      await add({ variables: { input: { id: guideId, appId: app.id, name: app.name ?? '' } } });
      setAdded((x) => new Set(x).add(guideId));
    } catch {
      // The error is shown in the dialog from the mutation's state.
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth={true}>
      <DialogTitle>
        Koble anvisninger til {app.name}
        <FormGroup>
          <FormControlLabel control={<Checkbox onChange={(e) => setShowExpired(e.target.checked)} checked={showExpired} />} label="Vis utgåtte" />
        </FormGroup>
        <SearchField filter={setFilter} startSearch={filter} />
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">Kunne ikke koble anvisningen til applikasjonen. Prøv igjen.</Alert>}
        {loading && <LoadingSpinner />}
        {!loading && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Nummer</TableCell>
                <TableCell>Tittel</TableCell>
                <TableCell>Fagområde</TableCell>
                <TableCell>Tagger</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {guides.map((guide) => {
                if (!guide) return null;
                const linked = linkedGuideIds.has(guide.id) || added.has(guide.id);
                return (
                  <TableRow key={guide.id}>
                    <TableCell>
                      <Button variant="contained" size="small" disabled={linked} onClick={() => handleAdd(guide.id)} sx={{ whiteSpace: 'nowrap' }}>
                        {linked ? 'Koblet' : 'Koble til'}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <StatusChip guide={guide} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{guide.docName}</TableCell>
                    <TableCell>{guide.docTitle}</TableCell>
                    <TableCell>{guide.mainCategory?.title}</TableCell>
                    <TableCell>
                      <TagsCell guide={guide} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Lukk</Button>
      </DialogActions>
    </Dialog>
  );
}
